// Часовые пояса объектов. Смена целиком относится к дате заступления по поясу ОБЪЕКТА
// (ПРАВИЛА.md, раздел 3): объекты клиента стоят от Перми до Иркутской области, и вахта,
// заступившая в 20:00 в Иркутске, по московскому времени попала бы во вчера.
//
// Список закрытый и короткий: это не выбор из четырёхсот зон IANA, а перечень поясов, в
// которых у клиента есть объекты, плюс запас на соседние. Пояс, приехавший с сервера и не
// попавший в список, показывается как есть — молча подменять его на московский нельзя.

export interface TimezoneOption {
  /** Идентификатор IANA — то, что уходит на сервер. */
  readonly id: string;
  /** Как это называет человек ПРИ ВЫБОРЕ: город и смещение. */
  readonly title: string;
  /** На сколько часов впереди Москвы. Отрицательное — позади. */
  readonly toMoscow: number;
}

export const SITE_TIMEZONES: readonly TimezoneOption[] = [
  { id: 'Europe/Kaliningrad', title: 'Калининград (UTC+2)', toMoscow: -1 },
  { id: 'Europe/Moscow', title: 'Москва (UTC+3)', toMoscow: 0 },
  { id: 'Europe/Samara', title: 'Самара (UTC+4)', toMoscow: 1 },
  { id: 'Asia/Yekaterinburg', title: 'Екатеринбург, Пермь (UTC+5)', toMoscow: 2 },
  { id: 'Asia/Omsk', title: 'Омск (UTC+6)', toMoscow: 3 },
  { id: 'Asia/Krasnoyarsk', title: 'Красноярск (UTC+7)', toMoscow: 4 },
  // Томск отдельным пунктом, хотя смещение то же, что у Красноярска: в базе объекты стоят с
  // поясом `Asia/Tomsk`, и без строки в списке он показывался человеку как есть — «Asia/Tomsk»
  // посреди русских названий.
  { id: 'Asia/Tomsk', title: 'Томск (UTC+7)', toMoscow: 4 },
  { id: 'Asia/Irkutsk', title: 'Иркутск (UTC+8)', toMoscow: 5 },
  { id: 'Asia/Yakutsk', title: 'Якутск (UTC+9)', toMoscow: 6 },
  { id: 'Asia/Vladivostok', title: 'Владивосток (UTC+10)', toMoscow: 7 },
];

/** Пояс по умолчанию для нового объекта — тот же, что у сервера в контракте. */
export const DEFAULT_TIMEZONE = 'Europe/Moscow';

/**
 * Пояс объекта ДЛЯ ПОКАЗА в карточке — не тем же текстом, что при выборе.
 *
 * При выборе нужен город: человек ищет в списке знакомое название. В карточке уже открытого
 * объекта город пояса — чужой: у объекта в Сургуте писалось «Екатеринбург, Пермь (UTC+5)», то
 * есть два не относящихся к делу города и техническая запись впридачу. Из того же списка её
 * убрали в реестре объектов ещё раньше, в карточке она осталась.
 *
 * Человеку здесь нужен один факт: насколько день объекта расходится с московским. Именно об
 * этом и говорит пояснение строкой ниже — смена относится к дате заступления по поясу объекта,
 * а не по московскому.
 */
export function timezoneAsMoscowDiff(id: string): string {
  const зона = SITE_TIMEZONES.find((item) => item.id === id);
  if (зона === undefined) {
    // Неизвестный пояс отдаём как есть: молча подменять его на московский нельзя.
    return id;
  }
  if (зона.toMoscow === 0) {
    return 'как в Москве';
  }
  const часов = Math.abs(зона.toMoscow);
  const слово = часов === 1 ? 'час' : часов < 5 ? 'часа' : 'часов';
  return зона.toMoscow > 0
    ? `на ${часов} ${слово} впереди Москвы`
    : `на ${часов} ${слово} позади Москвы`;
}
